import { centsToDollar } from '@utils/currency';
import {
  COUPON_DISCOUNT,
  VIP_DISCOUNT,
} from '@shared/types/transactionLineItems';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import styles from './CheckoutReceiptDetails.scss';

// Todo: Consolidate duplicate interfaces
interface TransactionLineItem {
  name: string;
  pricePerUnit: number;
  quantity: number;
}

export interface CheckoutReceiptDetailsProps {
  className?: string;
  fee: number;
  tax: number;
  total: number;
  hideTaxesAndFees?: boolean;
  hideZeroAmounts?: boolean;
  /** Hides only the processing fee when it is $0, as when the restaurant pays it instead of the guest. */
  hideZeroFee?: boolean;
  lineItems: TransactionLineItem[];
  totalLabel?: string;
  variant?: 'default' | 'compact';
}

export const CheckoutReceiptDetails = ({
  className,
  fee,
  tax,
  total,
  hideTaxesAndFees = false,
  hideZeroAmounts = false,
  hideZeroFee = false,
  lineItems = [],
  totalLabel = 'Total',
  variant = 'default',
}: CheckoutReceiptDetailsProps) => {
  const isCompact = variant === 'compact';

  const visibleLineItems = hideZeroAmounts
    ? lineItems.filter(
        (lineItem) => lineItem.pricePerUnit * lineItem.quantity !== 0,
      )
    : lineItems;
  const showTax = !hideTaxesAndFees && (!hideZeroAmounts || tax !== 0);
  const showFee =
    !hideTaxesAndFees && (!(hideZeroAmounts || hideZeroFee) || fee !== 0);

  return (
    <div className={className}>
      {visibleLineItems.map((lineItem) => (
        <div
          className={cx(styles.row, styles.price, typography.h7_20, {
            [styles.couponDiscount]:
              lineItem.name === COUPON_DISCOUNT ||
              lineItem.name === VIP_DISCOUNT,
            [styles.compactRow]: isCompact,
          })}
          key={lineItem.name}
          data-testid="line-item"
        >
          <div>
            {lineItem.name} {lineItem.quantity > 1 && `x${lineItem.quantity}`}
          </div>
          <div className={cx({ [styles.compactValue]: isCompact })}>
            {centsToDollar(lineItem.pricePerUnit * lineItem.quantity)}
          </div>
        </div>
      ))}

      {showTax && (
        <div
          className={cx(styles.row, typography.c3, styles.subText, {
            [styles.compactRow]: isCompact,
          })}
        >
          <div>Tax</div>
          <div className={cx({ [styles.compactValue]: isCompact })}>
            {centsToDollar(tax)}
          </div>
        </div>
      )}

      {showFee && (
        <div
          className={cx(styles.row, typography.c3, styles.subText, {
            [styles.compactRow]: isCompact,
          })}
        >
          <div>Processing</div>
          <div className={cx({ [styles.compactValue]: isCompact })}>
            {centsToDollar(fee)}
          </div>
        </div>
      )}

      <div
        className={cx(styles.row, styles.total, {
          [styles.compactTotal]: isCompact,
        })}
      >
        <div
          className={cx(typography.h5, { [styles.compactLabel]: isCompact })}
        >
          {totalLabel}
        </div>
        <div
          className={cx(typography.h6, {
            [styles.compactTotalValue]: isCompact,
          })}
        >
          {centsToDollar(total)}
        </div>
      </div>
    </div>
  );
};
